import * as React from 'react';
import {trigger} from '@pluto-tv/assemble';

export function useMultiSelect<T>(
  items: T[],
  selectedItems: T[],
  setSelectedItems: (item: T[]) => void,
): [(indexes: number[]) => void] {
  const shiftPressedRef = React.useRef(false);
  const ctrlPressedRef = React.useRef(false);
  const lastItemClickedRef = React.useRef<number[]>([]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        shiftPressedRef.current = true;
      }
      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlPressedRef.current = true;
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        shiftPressedRef.current = false;
      }
      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlPressedRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleClickItems = React.useCallback(
    (indexes: number[]) => {
      // ✅ make a copy and sort numerically (do not mutate input)
      const indexesSorted = [...indexes].sort((a, b) => a - b);

      if (shiftPressedRef.current && lastItemClickedRef.current.length) {
        // Range select between last clicked and current
        const selected: T[] = [...selectedItems];
        const lastItemClicked = lastItemClickedRef.current[lastItemClickedRef.current.length - 1];

        const start = Math.min(lastItemClicked, indexesSorted[0]);
        const end = Math.max(lastItemClicked, indexesSorted[indexesSorted.length - 1]);

        for (let i = start; i <= end; i++) {
          const candidate = items[i];
          if (candidate && !selected.includes(candidate)) {
            selected.push(candidate);
          }
        }

        if (start !== lastItemClicked) selected.reverse();

        setSelectedItems(selected);
        trigger('selectedTimelineEntries', selected);
      } else if (ctrlPressedRef.current) {
        // Toggle specific indexes
        const indexSet = new Set(indexesSorted);

        const updatedSelectedItems = (() => {
          // if ANY of these indexes is already selected, remove them; otherwise add them
          const anyAlreadySelected = selectedItems.some(item => indexSet.has(items.indexOf(item)));
          if (anyAlreadySelected) {
            return selectedItems.filter(item => !indexSet.has(items.indexOf(item)));
          } else {
            return [...selectedItems, ...indexesSorted.map(i => items[i]).filter(Boolean)];
          }
        })();

        setSelectedItems(updatedSelectedItems);
        trigger('selectedTimelineEntries', updatedSelectedItems);

        // keep last clicked indices list sorted
        if (updatedSelectedItems.length >= selectedItems.length) {
          // we added
          lastItemClickedRef.current = [...lastItemClickedRef.current, ...indexesSorted].sort((a, b) => a - b);
        } else {
          // we removed
          lastItemClickedRef.current = lastItemClickedRef.current.filter(i => !indexSet.has(i)).sort((a, b) => a - b);
        }
      } else {
        // --- SIMPLE CLICK: single-select with toggle ---
        const isSingle = indexesSorted.length === 1;
        const i = indexesSorted[0];
        const el = items[i];

        if (isSingle && selectedItems.includes(el)) {
          /* NOOP: TEMPORARILY disable click to un-select:
          We need to maintain the if block but do nothing to preserve functionality
          const nextSelection: T[] = selectedItems.filter(x => x !== el);
          setSelectedItems(nextSelection);
          trigger('selectedTimelineEntries', nextSelection);
          lastItemClickedRef.current = [i];
          */
        } else {
          const nextSelection = indexesSorted.map(idx => items[idx]).filter(Boolean);
          setSelectedItems(nextSelection);
          trigger('selectedTimelineEntries', nextSelection);
          lastItemClickedRef.current = indexesSorted;
        }
      }
    },
    [items, selectedItems, setSelectedItems],
  );

  return [handleClickItems];
}
